import {isTooComplex} from '@apps-common/geocorn-sdk/geom/simplify';
import {createReducer} from '@reduxjs/toolkit';
import GeoJSON from 'ol/format/GeoJSON';

import * as searchActions from '../actions/search.js';
import {resetTour, setTour} from '../actions/tour.js';
import {TOURS} from '../constants/index.js';
import getDateRanges from '../util/get-date-ranges.js';
import hasUnlimitedDate from '@explorer/util/check-for-no-end-date';
import {dateRangesToISO, getGeometry} from '@explorer/util/filters';
import {computeGeometryTooLarge, createGeometryObj} from '@explorer/util/geom';

const formatGeoJSON = new GeoJSON();

const resetState = {
  dailyEmailEnabled: false,
  geometry: null,
  geometryId: null,
  geometryTooLarge: true,
  geometryZoomTo: true,
  id: null,
  isGeometryTooComplex: false,
  name: '',
  noEndDate: false,
  page: 0,
  singleItemSearchId: null,
  featureReference: null,
};

export const initialState = {
  dateRanges: [],
  maxVertexCount: 1500,
  ...resetState,
};

const search = createReducer(initialState, builder => {
  builder.addCase(searchActions.saveGeometry, (state, {payload}) => {
    const {
      geometry,
      geometryTooLarge,
      geometryZoomTo,
      isGeometryTooComplex,
      name,
      featureReference,
    } = payload;

    state.geometry = geometry;
    (state.geometryId = state.geometryId === null ? 1 : state.geometryId + 1),
      (state.geometryTooLarge = geometryTooLarge);
    state.geometryZoomTo = geometryZoomTo;
    state.id = initialState.id;
    state.isGeometryTooComplex = isGeometryTooComplex;
    state.name = name;
    state.singleItemSearchId = initialState.singleItemSearchId;
    state.featureReference = featureReference;
  });

  builder.addCase(
    searchActions.setDateRanges,
    (state, {payload: dateRanges}) => {
      state.dateRanges = dateRangesToISO(dateRanges);
    }
  );

  builder.addCase(searchActions.continueSearch, state => {
    state.page = state.page + 1;
  });

  builder.addCase(searchActions.loadSearch, (state, {payload: search}) => {
    const dateRanges = getDateRanges(search.filter);
    const geometry = search.hydratedGeometry || getGeometry(search.filter);

    state.dailyEmailEnabled = search.__daily_email_enabled;
    state.id = search.id;
    state.dateRanges = dateRangesToISO(dateRanges);
    state.noEndDate = hasUnlimitedDate(dateRanges);
    state.name = search.name;
    state.geometry = geometry;
    state.geometryId = state.geometryId === null ? 1 : state.geometryId + 1;
    state.geometryTooLarge = geometry
      ? computeGeometryTooLarge(formatGeoJSON.readGeometry(geometry))
          .geometryTooLarge
      : false;
    state.geometryZoomTo = true;
    state.isGeometryTooComplex = geometry
      ? isTooComplex(geometry, state.maxVertexCount)
      : false;
    state.singleItemSearchId = initialState.singleItemSearchId;
    state.featureReference = search.featureReference;
  });

  builder.addCase(searchActions.searchSingleItem, (state, {payload: item}) => {
    const {newGeometry, geometryTooLarge} = createGeometryObj(item.geometry);

    state.geometry = newGeometry;
    state.geometryId = state.geometryId === null ? 1 : state.geometryId + 1;
    state.geometryTooLarge = geometryTooLarge;
    state.geometryZoomTo = true;
    state.isGeometryTooComplex = false;
    state.name = item.id;
    state.singleItemSearchId = item.id;
    state.featureReference = initialState.featureReference;
  });

  builder.addCase(searchActions.clearSearch, state => {
    return {
      ...state,
      ...resetState,
    };
  });

  builder.addCase(searchActions.resetDateRanges, state => {
    state.dateRanges = initialState.dateRanges;
    state.noEndDate = initialState.noEndDate;
  });

  builder.addCase(searchActions.toggleNoEndDate, (state, {payload}) => {
    state.noEndDate = typeof payload === 'boolean' ? payload : !state.noEndDate;
  });

  builder.addCase(searchActions.setIsGeometryTooComplex, (state, {payload}) => {
    state.isGeometryTooComplex = payload;
  });

  builder.addCase(searchActions.resetSearchPageNumber, state => {
    state.page = 0;
  });

  builder.addCase(searchActions.setSearchPageNumber, (state, {payload}) => {
    state.page = payload ?? 0;
  });

  builder.addCase(
    searchActions.executeSearch,
    (state, {payload: suggestion}) => {
      state.id = initialState.id;
      state.page = initialState.page;
      state.singleItemSearchId = initialState.singleItemSearchId;
      state.dailyEmailEnabled = false;
      state.featureReference = initialState.featureReference;

      if (suggestion) {
        const {newGeometry, geometryTooLarge} = createGeometryObj(
          suggestion.geometry
        );

        state.geometry = newGeometry;
        state.geometryId = state.geometryId === null ? 1 : state.geometryId + 1;
        state.geometryTooLarge = geometryTooLarge;
        state.geometryZoomTo = true;
        state.isGeometryTooComplex = false;
        state.name = suggestion.label || suggestion.title;
      }
    }
  );

  builder.addCase(setTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });

  builder.addCase(resetTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });
});

export default search;
